import {
  HttpClient,
  HttpClientRequest,
  HttpClientResponse,
} from "@effect/platform";
import { Array, BigInt, Duration, Effect, Option, pipe } from "effect";
import { MerklCampaignMeta, MerklCampaignsResponse } from "./types";
import {
  Address,
  BaseError,
  BaseErrorParams,
  ChainId,
  getChainKey,
  makeAmount,
  PositionType,
  ProtocolKey,
} from "@silo/types";
import { Schema } from "@effect/schema";
import { getPricingToken } from "./get-pricing-token";
import { match } from "ts-pattern";
import { AGLA_MERKL_ADDRESS, SUPERFEST_TAG } from "./constants";
import { KvClient } from "../kv-client";
import { MarketChainMap } from "@silo/utils";
import { RewardToken } from "../types";
import { setMerklHistoricalRewardTokenArray } from "../kv-client/merkl-user-rewards-info";

export type GetCampaignMetaArray = ReturnType<typeof getCampaignMetaArray>;
export type GetCampaignMetaArraySuccess =
  Effect.Effect.Success<GetCampaignMetaArray>;
export type GetCampaignMetaArrayError =
  Effect.Effect.Error<GetCampaignMetaArray>;
export type GetCampaignMetaArrayContext =
  Effect.Effect.Context<GetCampaignMetaArray>;

interface GetCampaignMetaArrayParams {
  activeOnly: boolean;
}

type RawCampaign = MerklCampaignsResponse[string][string][string];

export function getCampaignMetaArray({
  activeOnly,
}: GetCampaignMetaArrayParams) {
  return Effect.gen(function* () {
    const [merklCampaignsResponse, marketProtocolKeyMap] = yield* Effect.all(
      [
        pipe(
          HttpClientRequest.get("https://api.merkl.xyz/v3/campaigns?types=5"),
          HttpClient.fetch,
          Effect.andThen(
            HttpClientResponse.schemaBodyJson(MerklCampaignsResponse)
          ),
          Effect.scoped
        ),
        KvClient.getMarketProtocolKeyMap(),
      ],
      { concurrency: "unbounded" }
    );

    const rawCampaigns: RawCampaign[] = [];

    for (const chain of Object.values(merklCampaignsResponse)) {
      for (const parameter of Object.values(chain)) {
        for (const campaign of Object.values(parameter)) {
          rawCampaigns.push(campaign);
        }
      }
    }

    const [excluded, satisfying] = yield* Effect.partition(
      rawCampaigns,
      (rawCampaign) => getCampaignMeta(rawCampaign, marketProtocolKeyMap)
    );

    yield* excluded.length > 0
      ? Effect.logWarning(
          `Count of invalid merkl campaigns: ${excluded.length}`
        )
      : Effect.void;

    yield* Effect.all(excluded.map((error) => Effect.logDebug(error.message)));

    const rewardTokenArray: RewardToken[] = satisfying.map(
      ({ chainKey, pricingTokenAddress, pricingTokenSymbol }) => ({
        chainKey,
        tokenAddress: pricingTokenAddress,
        symbol: pricingTokenSymbol,
      })
    );

    yield* setMerklHistoricalRewardTokenArray(rewardTokenArray);

    const filteredCampaigns = satisfying.filter((campaign) => {
      if (!activeOnly) return true;

      return campaign.isActive;
    });

    return filteredCampaigns;
  });
}

interface InvalidCampaignErrorParams extends BaseErrorParams {
  campaignId: string;
}

export class InvalidCampaignError extends BaseError {
  public readonly _tag = "InvalidCampaignError";
  public readonly campaignId: string;

  constructor({ campaignId, ...rest }: InvalidCampaignErrorParams) {
    super(rest);

    this.campaignId = campaignId;
  }
}

export type GetCampaignMeta = ReturnType<typeof getCampaignMeta>;
export type GetCampaignMetaSuccess = Effect.Effect.Success<GetCampaignMeta>;
export type GetCampaignMetaError = Effect.Effect.Error<GetCampaignMeta>;
export type GetCampaignMetaContext = Effect.Effect.Context<GetCampaignMeta>;

export function getCampaignMeta(
  rawCampaign: RawCampaign,
  marketProtocolKeyMap: MarketChainMap<ProtocolKey>
) {
  const { campaignId } = rawCampaign;

  return Effect.gen(function* () {
    const chainKey = yield* pipe(
      rawCampaign.chainId,
      Schema.validate(ChainId),
      Effect.andThen(getChainKey)
    );

    const {
      campaignParameters,
      amount,
      apr,
      tags,
      startTimestamp,
      endTimestamp,
    } = rawCampaign;

    const marketAddress = yield* pipe(
      campaignParameters.whitelist,
      Array.get(0),
      Effect.mapError(() => new Error("Missing whitelist market address")),
      Effect.andThen(Schema.decode(Address))
    );

    const protocolKey = yield* pipe(
      marketProtocolKeyMap.get({ chainKey, marketAddress }),
      Effect.mapError(
        () => new Error(`Protocol key not found: ${chainKey}-${marketAddress}`)
      )
    );

    const tokenAddress = yield* pipe(
      campaignParameters.targetToken,
      Schema.decode(Address)
    );

    const rewardTokenAddress = yield* pipe(
      rawCampaign.rewardToken,
      Schema.decode(Address)
    );

    yield* rewardTokenAddress === AGLA_MERKL_ADDRESS
      ? Effect.fail(new Error("agla merkl address"))
      : Effect.void;

    const rewardTokenSymbol = campaignParameters.symbolRewardToken;

    const { pricingTokenAddress, pricingTokenSymbol } = getPricingToken({
      chainKey,
      rewardTokenAddress,
      rewardTokenSymbol,
    });

    const { duration } = campaignParameters;

    const amountPerSecond = pipe(
      BigInt.divide(amount, duration),
      Option.getOrElse(() => 0n),
      makeAmount
    );

    const positionType = match(rawCampaign.campaignSubType)
      .returnType<PositionType>()
      .with(0, () => "collateral")
      .with(1, () => "protected")
      .with(2, () => "debt")
      .exhaustive();

    const isSuperFest = tags.includes(SUPERFEST_TAG);

    const now = pipe(Date.now(), Duration.millis, Duration.toSeconds);
    const isActive = now >= startTimestamp && now <= endTimestamp;

    const merklCampaignMeta: MerklCampaignMeta = {
      protocolKey,
      chainKey,
      marketAddress,
      tokenAddress,
      rewardTokenAddress,
      rewardTokenSymbol,
      rewardTokenDecimals: campaignParameters.decimalsRewardToken,
      pricingTokenAddress,
      pricingTokenSymbol,
      amount,
      duration,
      amountPerSecond,
      positionType,
      merklApr: apr,
      isSuperFest,
      isActive,
    };

    return merklCampaignMeta;
  }).pipe(
    Effect.mapError(
      (cause) =>
        new InvalidCampaignError({
          cause,
          campaignId,
          message: `Invalid campaign: ${campaignId}`,
        })
    )
  );
}
